import React from "react";

import { ConfirmModal, ContextMenu } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useContextMenus } from "@/hooks/useContextMenus";
import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useViewActions } from "@/hooks/useViewActions";

import { TViewCtxMenuData } from "@/contexts";

export const ViewsCtxMenu: React.FC = () => {
	const { viewId: shownViewId } = useHomeLocation();
	const { toView } = useHomeNavigation();
	const pdf = usePlanGate("pdf");
	const navigate = useNavigate();
	const { archive, remove } = useViewActions();
	const { views: menu } = useContextMenus();
	const [showConfirmModal, setShowConfirmModal] =
		React.useState<TViewCtxMenuData>();

	const handleEdit = (view: TViewCtxMenuData) => {
		navigate(`/views/${view.id}?edit=1`);
	};

	const handleRemove = (view?: TViewCtxMenuData, confirmed = false) => {
		if (!confirmed) {
			setShowConfirmModal(view);
			return;
		}

		return remove(view!).then(() => {
			// The home page was on it: to the default view.
			if (shownViewId === view!.id) toView();
		});
	};

	const handleArchive = (view: TViewCtxMenuData) =>
		archive(view).then(() => {
			// An archived view leaves the landing choice: off it if shown.
			if (!view.isArchived && shownViewId === view.id) toView();
		});

	const handleAddProject = (view: TViewCtxMenuData) => {
		navigate(`/views/${view.id}?new=1`);
	};

	return (
		<div>
			<ContextMenu
				menu={menu}
				items={(view) => [
					{
						label: t("projects.add"),
						picto: "addCircle",
						onClick: () => handleAddProject(view),
					},
					{
						label: t("tasks.selectMulti"),
						picto: "copy",
						hidden: view.from !== "taskList",
						onClick: () => view.onSelect?.(),
					},
					{
						label: t("reports.generate"),
						// A plan feature, not a role: off-plan it locks and
						// opens the upgrade modal (the API refuses it anyway).
						picto: pdf.locked ? "lock" : "briefcase",
						onClick: pdf.locked
							? pdf.open
							: () => navigate(`/reports/new?viewId=${view.id}`),
					},
					{
						label: t("actions.infos"),
						picto: "info",
						onClick: () => navigate(`/views/${view.id}`),
					},
					{
						label: t("actions.archive"),
						picto: "archive",
						hidden: view.isArchived,
						onClick: () => void handleArchive(view),
					},
					{
						label: t("actions.unarchive"),
						picto: "archive",
						hidden: !view.isArchived,
						onClick: () => void handleArchive(view),
					},
					{
						label: t("actions.settings"),
						picto: "settings",
						onClick: () => handleEdit(view),
					},
					{
						label: t("actions.delete"),
						picto: "trash",
						color: "danger",
						onClick: () => handleRemove(view),
					},
				]}
			/>
			<ConfirmModal
				open={!!showConfirmModal}
				onClose={() => {
					setShowConfirmModal(undefined);
				}}
				onConfirm={() => {
					handleRemove(showConfirmModal, true);
					setShowConfirmModal(undefined);
				}}
				title={t("views.confirmDelete.title")}
				confirmText={t("actions.delete")}
				cancelText={t("actions.cancel")}
				confirmColor="danger"
			>
				<Trans
					i18nKey="views.confirmDelete.message"
					values={{
						name: showConfirmModal?.name,
					}}
					components={{ b: <b /> }}
				/>
			</ConfirmModal>
		</div>
	);
};
